import isNil from 'lodash/isNil';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';

export class GateStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  depthConfig: { id: number; params: any[]; precision: number } | undefined;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}_${quote.toUpperCase()}`;
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL(`https://www.gate.io/apiw/v2/market/currency_pairs/${this.pair}`);

      const res = await proxyAxios.get<{ data: { precision: number } }>('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      if (res.data) {
        this.precision = res.data.data.precision;

        this.precisionUpdate(res.data.data.precision);
      } else {
        this.precisionUpdate(1);
      }
    } catch (error) {
      console.log('gate ticker error: ', error);
    }
  };

  fetchOrderbook = async ({ precision }: { precision: number }) => {
    try {
      const url = new URL('https://api.gateio.ws/api/v4/spot/order_book');
      const params = new URLSearchParams({
        currency_pair: this.pair,
        limit: this.depth.toString(),
        interval: normalizeNumber(1 / 10 ** precision),
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{ asks: [string, string][]; bids: [string, string][] }>(
        '/api/v1/proxy',
        {
          params: { url: url.toString() },
        },
      );

      if (res.data) {
        const precision = isNil(this.depthConfig) ? this.precision : this.depthConfig.precision;

        const orderbook = {
          bids: res.data.bids.map(el => [Number(el[0]).toFixed(precision), el[1]]),
          asks: res.data.asks.map(el => [Number(el[0]).toFixed(precision), el[1]]),
        };

        this.onOrderbookUpdate(orderbook);

        return orderbook;
      }
    } catch (error) {
      console.log('Gate orderbook error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    from,
    to,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.gate];

    try {
      const url = new URL('https://api.gateio.ws/api/v4/spot/candlesticks');
      const params = new URLSearchParams({
        currency_pair: `${base}_${quote}`,
        limit: limit.toString(),
        interval: innerInterval,
        from: Math.floor(from / 1000).toString(),
        to: Math.floor(to / 1000).toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<string[][]>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (result.data ?? []).map(el => [
        Number(el[0]) * 1000,
        el[5],
        el[3],
        el[4],
        el[2],
        el[6],
      ]) as KLine[];
    } catch (error) {
      console.log('gate klines error: ', error);
      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://api.gateio.ws/api/v4/spot/trades');
      const params = new URLSearchParams({
        currency_pair: `${base}_${quote}`,
        limit: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<
        {
          side: string;
          price: string;
          amount: string;
          create_time: string;
        }[]
      >('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      return (
        result.data?.map(item => ({
          price: item.price,
          quantity: item.amount,
          timestamp: item.create_time,
          side: item.side,
        })) ?? []
      );
    } catch (error) {
      console.log('Gate trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://ws.gate.io/v3/', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send(
          JSON.stringify({ id: Math.floor(Date.now() / 100), method: 'server.ping', params: [] }),
        );
      }
    }, 60 * 1000);

    this.ws.onopen = () => {
      const depthId = Date.now();
      const tickerId = depthId + 1;
      const klinesId = depthId + 2;
      const tradesId = Date.now();

      this.depthConfig = {
        params: [this.pair, this.depth, normalizeNumber(1 / 10 ** this.precision)],
        id: depthId,
        precision: this.precision,
      };

      ws.send(
        JSON.stringify({
          id: this.depthConfig.id,
          method: 'depth.subscribe',
          params: this.depthConfig.params,
        }),
      );

      ws.send(
        JSON.stringify({
          id: tickerId,
          method: 'ticker.subscribe',
          params: [this.pair],
        }),
      );

      ws.send(
        JSON.stringify({
          id: klinesId,
          method: 'kline.subscribe',
          params: [[this.pair, Math.floor(interval / 1000)]],
        }),
      );

      ws.send(
        JSON.stringify({
          id: tradesId,
          method: 'trades.subscribe',
          params: [this.pair],
        }),
      );
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.method === 'depth.update' && data?.params?.[1]) {
          const { asks, bids } = data?.params?.[1];

          const precision = isNil(this.depthConfig) ? this.precision : this.depthConfig.precision;

          this.onOrderbookUpdate({
            bids: (bids as [string, string][]).map(el => [Number(el[0]).toFixed(precision), el[1]]),
            asks: (asks as [string, string][]).map(el => [Number(el[0]).toFixed(precision), el[1]]),
          });
        }

        if (data.method === 'ticker.update' && data?.params?.[1]) {
          const { baseVolume, change, high, last, low, quoteVolume } = data?.params?.[1];

          this.onTickerUpdate({
            baseVolume: quoteVolume,
            change,
            close: last,
            high,
            last,
            low,
            quoteVolume: baseVolume,
          });
        }

        if (data.method === 'kline.update' && data?.params?.[0]) {
          const [time, open, close, high, low, volume] = data.params[0];

          this.klineUpdate([time, open, high, low, close, volume]);
        }

        if (data.method === 'trades.update' && data?.params?.[1]) {
          data?.params?.[1].forEach(
            ({
              amount,
              price,
              time,
              type,
            }: {
              amount: string;
              price: string;
              time: string;
              type: string;
            }) => {
              this.onTradeUpdate({
                price: price,
                quantity: amount,
                timestamp: time,
                side: type,
              });
            },
          );
        }
      } catch (error) {
        console.log('gate socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  setOrderbookDepth(depth: number) {
    this.depth = depth;

    this.fetchOrderbook({ precision: this.precision });

    if (this.ws && this.depthConfig) {
      this.ws.send(
        JSON.stringify({
          id: this.depthConfig.id,
          method: 'depth.unsubscribe',
          params: this.depthConfig.params,
        }),
      );

      this.depthConfig = {
        params: [this.pair, this.depth, normalizeNumber(1 / 10 ** this.precision)],
        id: Date.now(),
        precision: this.precision,
      };

      this.ws.send(
        JSON.stringify({
          id: this.depthConfig.id,
          method: 'depth.subscribe',
          params: this.depthConfig.params,
        }),
      );
    }
  }

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.fetchOrderbook({ precision: tick });

    if (this.ws && this.depthConfig) {
      this.ws.send(
        JSON.stringify({
          id: this.depthConfig.id,
          method: 'depth.unsubscribe',
          params: this.depthConfig.params,
        }),
      );

      this.precision = tick;

      this.depthConfig = {
        params: [this.pair, this.depth, normalizeNumber(1 / 10 ** tick)],
        id: this.depthConfig.id + 10,
        precision: tick,
      };

      this.ws.send(
        JSON.stringify({
          id: this.depthConfig.id,
          method: 'depth.subscribe',
          params: this.depthConfig.params,
        }),
      );
    }
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook({ precision: this.precision });
  };

  static init = async (args: IInitArgs) => {
    const instance = new GateStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
